import axios, { type AxiosError } from 'axios';
import { getAccessToken } from '../../../auth/oktaConfig';
import type { IRecipient } from '../../../common/recipients/types';
import { API_URL } from '../../environment-config';
import type { IResponse } from '../../types';
import { headers } from '../common/helper';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getRecipients = async (envelopeId: string, allRecipients = false): Promise<IRecipient[]> => {
  const accessToken = await getAccessToken();
  const getAllRecipients = allRecipients ? '&allRecipients' : '';
  const { data } = await axios.get(
    `${API_URL}/envelopes/${envelopeId}/recipients?api-version=2.0${getAllRecipients}`,
    headers(accessToken)
  );
  return data;
};

export async function updateRecipients(recipients: IRecipient[], envelopeId: string): Promise<IResponse<IRecipient[]>> {
  const accessToken = await getAccessToken();
  const newRecipients = recipients.map((r) => {
    return {
      ...r,
      ...(r.group && { group: r.group!.order }),
      envelopeId,
    };
  });

  try {
    const res = await axios.put<IRecipient[]>(
      `${API_URL}/envelopes/${envelopeId}/recipients?api-version=2.0`,
      newRecipients,
      headers(accessToken)
    );
    return { success: true, result: res.data, statusCode: res.status };
  } catch (e) {
    const err = e as AxiosError;
    // Depending on the type of error, this endpoint can return a
    // couple different shapes unfortunately
    const errResponse = err.response?.data as
      | { errors: Record<string, string[]>; status: number }
      | { errors: string[]; statusCode: number };

    const errorList = Array.isArray(errResponse.errors ?? [])
      ? (errResponse.errors as string[])
      : Object.entries(errResponse.errors as Record<string, string[]>)
          .map(([key, value]) => {
            const arr = Array.isArray(value) ? value : [value];
            return arr.map((v) => `${key}:${v}`);
          })
          .flat();
    return { success: false, statusCode: err.response?.status ?? 500, errors: errorList };
  }
}

export async function updateRecipient(recipient: IRecipient, envelopeId: string): Promise<IRecipient> {
  const accessToken = await getAccessToken();
  const updatedRecipient = {
    firstName: recipient.firstName,
    lastName: recipient.lastName,
    email: recipient.email,
    role: recipient.role,
    isActive: recipient.isActive,
    isPlaceHolder: recipient.isPlaceholder,
  };
  try {
    return await axios.patch(
      `${API_URL}/envelopes/${envelopeId}/recipients/${recipient.id}?api-version=2.0`,
      updatedRecipient,
      headers(accessToken)
    );
  } catch ({ response }) {
    return response;
  }
}
